import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import './LeaveRequests.css';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axiosInstance';
import { toast } from 'react-toastify';
import { Spinner, Alert } from 'react-bootstrap';

const LeaveRequests = ({ adminIds, empRole }) => {
  const { t } = useTranslation();

  useEffect(() => {
    //console.log("Employee IDs managed by the logged-in user:", adminIds);
  }, [adminIds]);

  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filter, setFilter] = useState('All');
  const [fileExistence, setFileExistence] = useState({});
  const [popupMessage, setPopupMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state

  axiosInstance.defaults.timeout = 120000; // Increase timeout to 120 seconds

  useEffect(() => {
    setLoading(true);
    const fetchLeaveRequests = async () => {
      try {
        const response = await axiosInstance.get('/admin/leaveRequests');
        let filteredRequests;

        if (empRole === 'SuperAdmin') {
          // Set all leave requests for SuperAdmin, excluding 'Withdrawn'
          filteredRequests = response.data.filter(request => request.leave_status !== 'Withdrawn');
        } else if (adminIds.length > 0) {
          // Filter leave requests based on adminIds and exclude 'Withdrawn'
          filteredRequests = response.data.filter(request =>
            adminIds.includes(parseInt(request.emp_id)) && request.leave_status !== 'Withdrawn'
          );
        } else {
          filteredRequests = [];
        }

        setLeaveRequests(filteredRequests.map(formatDates));
      } catch (error) {
        // Check if error response is available
        if (!error.response) {
          // Network error or no response
          toast.error("Looks like you are offline. Please check your internet connectivity.");
        } else {
          console.error('Error fetching leave requests:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveRequests();
  }, [adminIds, empRole]); // Add empRole as a dependency


  const formatDates = (request) => {
    const startDate = new Date(request.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const endDate = new Date(request.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return { ...request, start_date: startDate, end_date: endDate };
  };

  const leaveTypeFullName = (typeAbbreviation) => {
    switch (typeAbbreviation) {
      case 'earned_leave':
        return t("leaveRequest_earned");
      case 'on_duty':
        return t("leaveRequest_onDuty");
      case 'comp_off':
        return t("leaveRequest_compoff");
      case 'casual_leave':
        return t("leaveRequest_casual");
      case 'sick_leave':
        return t("leaveRequest_sick");
      case 'paternity_leave':
        return t("leaveRequest_paternity");
      case 'loss_of_pay':
        return t("leaveRequest_loss");
      default:
        return typeAbbreviation;
    }
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const filteredLeaveRequests = filter === 'All' ? leaveRequests : leaveRequests.filter(request => request.leave_status === filter);

  const getLeaveBalance = async (emp_id, leave_type) => {
    try {
      const response = await axiosInstance.get(`/admin/leaveData/${emp_id}`);
      if (response.status === 200) {
        return response.data[leave_type] || 0;
      }
    } catch (error) {
      console.error('Error fetching leave balance:', error);
      return 0;
    }
  };

  const handleApprove = async (id, emp_id, leave_type, num_of_days) => {
    const leaveBalance = await getLeaveBalance(emp_id, leave_type);

    if (num_of_days > leaveBalance) {
      setPendingApproval({ id, emp_id, leave_type, num_of_days });
      setIsModalOpen(true);
      return;
    }

    approveRequest(id);
  };

  const approveRequest = (id) => {
    axiosInstance.post(`/admin/leaveRequests/approve/${id}`)
      .then(response => {
        if (response.status === 200) {
          setLeaveRequests(prevRequests =>
            prevRequests.map(request => {
              if (request.id === id) {
                return { ...request, leave_status: 'Approved' };
              }
              return request;
            })
          );
        }
      })
      .catch(error => {
        console.error('Error approving leave request:', error);
      });
  };

  const handleDecline = (id) => {
    if (!id) {
      console.error('Invalid leave request id:', id);
      return;
    }

    axiosInstance.post(`/admin/leaveRequests/decline/${id}`)
      .then(response => {
        if (response.status === 200) {
          setLeaveRequests(prevRequests =>
            prevRequests.map(request => {
              if (request.id === id) {
                return { ...request, leave_status: 'Declined' };
              }
              return request;
            })
          );
        }
      })
      .catch(error => {
        console.error('Error declining leave request:', error);
      });
  };

  const handleDownload = async (id) => {
    setIsDownloading(true);
    setDownloadingId(id);
    try {
      const filenameResponse = await axiosInstance.get(`/user/getLeaveFilename/${id}`);
      if (filenameResponse.status !== 200) {
        throw new Error('Failed to fetch filename.');
      }
      const filename = filenameResponse.data.originalFilename;

      if (!filename) {
        throw new Error('Filename is null or undefined.');
      }

      const fileResponse = await axiosInstance.get(`/user/downloadLeaveFile/${id}`, {
        responseType: 'blob'
      });

      if (fileResponse.data.size === 0) {
        throw new Error('File is empty.');
      }

      const blob = new Blob([fileResponse.data], { type: fileResponse.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      setFileExistence(prev => ({ ...prev, [id]: true }));
    } catch (error) {
      setFileExistence(prev => ({ ...prev, [id]: false }));
      console.error('Error downloading file:', error.message);
    } finally {
      setIsDownloading(false);
      setDownloadingId(null);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setPendingApproval(null);
  };

  const confirmApproval = () => {
    if (pendingApproval) {
      approveRequest(pendingApproval.id);
    }
    closeModal();
  };

  return (
    <div className="leave-requests">
      <h2 className='pgTitle'>{t("leaveRequest_title")}</h2>

      <div className="filter-container">
        <select id="filter" value={filter} onChange={handleFilterChange}>
          <option value="All">{t("approval_all")}</option>
          <option value="Approved">{t("Approved")}</option>
          <option value="Declined">{t("Declined")}</option>
          <option value="Pending">{t("Pending")}</option>
        </select>
      </div>

      {popupMessage && <div className="popup-message">{popupMessage}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>{t("projectDetails_EmployeeID")}</th>
              <th>{t("attendance_name")}</th>
              <th>{t("leaveRequest_startDate")}</th>
              <th>{t("leaveRequest_endDate")}</th>
              <th>{t("leaveRequest_noDays")}</th>
              <th>{t("leaveRequest_type")}</th>
              <th>{t("approval_document")}</th>
              <th>{t("approval_status")}</th>
              <th>{t("approval_action")}</th>
            </tr>
          </thead>
          <tbody style={{ background: '#fff' }}>
            {loading ? (
              <tr>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td className="employee-table-Spinner centered">
                  <Spinner animation="border" role="status">
                    <span className=""></span>
                  </Spinner>
                  <span className="mt-2">{t('Please wait your data is loading')}</span>
                </td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
              </tr>
            ) : filteredLeaveRequests.length === 0 ? (
              <tr>
                <td colSpan="9" className="centered">
                  <Alert variant="light" className='employee-table-Alert'>
                    {t("No employees found")}
                  </Alert>
                </td>              </tr>
            ) : (
              filteredLeaveRequests.map(request => (
                <tr key={request.id}>
                  <td className='A-info'>{request.emp_sap_id}</td>
                  <td className='A-info'>{request.p_name}</td>
                  <td className='A-info'>{`${request.start_date}`}</td>
                  <td className='A-info'>{`${request.end_date}`}</td>
                  <td className='A-info'>{request.num_of_days}</td>
                  <td className='A-info'>{leaveTypeFullName(request.leave_type)}</td>
                  <td>
                    <button className='downBtns' onClick={() => handleDownload(request.id)} disabled={isDownloading && downloadingId === request.id}>
                      {isDownloading && downloadingId === request.id ? (
                        <span>Loading...</span>
                      ) : (
                        <FontAwesomeIcon icon={faDownload} />
                      )}
                    </button>
                    {fileExistence[request.id] === false && <p className="text-danger mt-2 mb-0">No file uploaded</p>}
                  </td>
                  <td className={`A-info status-${request.leave_status.toLowerCase()}`}>{t(`status_${request.leave_status.toLowerCase()}`)}</td>
                  <td className='A-info'>
                    {request.leave_status === 'Pending' && (
                      <>
                        <button className="approveButton" onClick={() => handleApprove(request.id, request.emp_id, request.leave_type, request.num_of_days)}>
                          <FaCheck />
                        </button>
                        <button className="declineButton" onClick={() => handleDecline(request.id)}>
                          <FaTimes />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        show={isModalOpen}
        onHide={closeModal}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("leaveRequest_noLeaves")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("leaveRequest_approveConfirm")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            {t("No")}
          </Button>
          <Button variant="primary" onClick={confirmApproval}>
            {t("Yes")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LeaveRequests;
